import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import AccountNavMenu from "@/components/AccountNavMenu";

// Section card primitive - mirrors RulesPage for visual consistency.
function Section({ title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-4 sm:p-6 mb-4"
    >
      <h2
        className="text-base sm:text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-3"
        style={{ fontFamily: "Fredoka" }}
      >
        {title}
      </h2>
      <div className="text-sm text-[#1A237E]/80 dark:text-[#E3F2FD]/80 space-y-2 leading-relaxed break-words">
        {children}
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-2 items-start">
      <span className="shrink-0 text-[#38B6FF] font-bold leading-relaxed">•</span>
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}

// Template content - clearly marked for legal review before launch.
export default function PrivacyPage() {
  const { t } = useTranslation();
  usePageTitle("Privacy Policy");
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8"
      id="main-content"
      role="main"
      tabIndex={-1}
      data-testid="privacy-page"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("common.back")}
          </Button>
          <AccountNavMenu />
        </div>
        <h1
          className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-2 break-words"
          style={{ fontFamily: "Fredoka" }}
        >
          <Shield className="w-6 h-6 text-[#38B6FF] shrink-0" />
          {t("footer.privacy")}
        </h1>
        <p className="text-xs sm:text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-6 italic">
          Last updated: this is a template. Have counsel review before launch.
        </p>

        <Section title="Overview" testId="privacy-overview">
          <p>
            Market Melee ("we", "us", "the Game") is a free-to-play,
            crypto-<em>themed</em> digital card game. The cryptocurrencies,
            prices, and portfolios in the Game are fictional. The Game does{" "}
            <strong>not</strong> involve real cryptocurrency, real money,
            wagering, or financial transactions of any kind.
          </p>
          <p>
            This Privacy Policy explains what information we collect, how we
            use it, and the choices you have. This document is a template
            and must be reviewed by qualified legal counsel before it is
            relied upon.
          </p>
        </Section>

        <Section title="Information We Collect" testId="privacy-collect">
          <ul className="space-y-1 pl-1">
            <Bullet>
              <strong>Account email.</strong> When you create an account we
              collect the email address you sign up with so we can identify
              your account, send account-related messages, and let you
              recover access.
            </Bullet>
            <Bullet>
              <strong>Gameplay statistics.</strong> We store data generated
              as you play - wins and losses, scores, match history, streaks,
              progression, and similar in-game stats - so the Game works and
              features like leaderboards and profiles can function.
            </Bullet>
            <Bullet>
              <strong>Cookies and session data.</strong> We use cookies and
              similar technologies to keep you signed in and to maintain
              authenticated sessions. See "Cookies" below.
            </Bullet>
          </ul>
          <p>
            We do not intentionally collect payment-card numbers, government
            IDs, or sensitive personal data, because the Game has no
            real-money component.
          </p>
        </Section>

        <Section title="How We Use Your Information" testId="privacy-use">
          <ul className="space-y-1 pl-1">
            <Bullet>To create, secure, and operate your account.</Bullet>
            <Bullet>
              To run core gameplay, save progress, and power features such
              as profiles, leaderboards, and match history.
            </Bullet>
            <Bullet>
              To keep you signed in across visits via session cookies.
            </Bullet>
            <Bullet>
              To detect, prevent, and address abuse, cheating, fraud, or
              technical problems.
            </Bullet>
            <Bullet>
              To send necessary account or service messages (for example,
              email verification or password resets).
            </Bullet>
          </ul>
        </Section>

        <Section title="Cookies" testId="privacy-cookies">
          <p>
            We use cookies primarily for authentication - to remember that
            you are signed in and to maintain your session securely. These
            are essential to operating the Game. You can control or delete
            cookies through your browser settings, but disabling
            authentication cookies will prevent you from signing in.
          </p>
        </Section>

        <Section title="How We Share Information" testId="privacy-share">
          <p>
            We do not sell your personal information. We may share limited
            information with service providers who help us host and operate
            the Game, and where required by law. Certain gameplay
            information you choose to make public - such as a display name or
            leaderboard ranking - may be visible to other players.
          </p>
        </Section>

        <Section title="Data Retention" testId="privacy-retention">
          <p>
            We keep account and gameplay data for as long as your account is
            active or as needed to provide the Game. You may request deletion
            of your account, after which we will delete or anonymize your
            data except where we must retain it for legal or security
            reasons.
          </p>
        </Section>

        <Section title="Your Choices" testId="privacy-choices">
          <ul className="space-y-1 pl-1">
            <Bullet>
              You may access or update certain account information from your
              profile and settings.
            </Bullet>
            <Bullet>
              You may request deletion of your account and associated data.
            </Bullet>
            <Bullet>
              You may manage cookies through your browser, subject to the
              limitations described above.
            </Bullet>
          </ul>
        </Section>

        <Section title="Children's Privacy" testId="privacy-children">
          <p>
            The Game is not directed to children under the age required by
            your local law to consent to online services, and we do not
            knowingly collect personal information from them. Adjust this
            section to your target jurisdictions with counsel.
          </p>
        </Section>

        <Section title="Changes to This Policy" testId="privacy-changes">
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will revise the "Last updated" date above and, where
            appropriate, provide additional notice.
          </p>
        </Section>

        <Section title="Contact Us" testId="privacy-contact">
          <p>
            Questions about this Privacy Policy or your data? Contact us
            through the support channel listed in the Game. (Replace with
            your real contact details before launch.)
          </p>
        </Section>
      </div>
    </div>
  );
}
